import { ResultData } from './rentLimits';
import { getRentLimits } from '@/components/ui/RentPrice/rentLimits';

type RentPriceResultStepProps = {
  inputData: ResultData;
};

const RentPriceResultStep = ({ inputData }: RentPriceResultStepProps) => {
  const rentLimits = getRentLimits(inputData.numberOfPersons, { ...inputData });

  if (!rentLimits) {
    return (
      <>
        <h1>Prüfung Mietangebot bzw. Neuanmietung</h1>
        <p>
          Vielen Dank für die Nutzung unseres Online-Services. Leider liegen ihre Werte außerhalb der Standardwerte, bitte vereinbaren sie
          einen persönlichen Termin, um ihren individuellen Fall zu klären.
        </p>
      </>
    );
  }

  const rentInLimit = inputData.rentPriceCold <= rentLimits.rentPriceCold;
  const sideCostInLimit = inputData.sideCosts <= rentLimits.sideCosts;

  return (
    <>
      <h1>Prüfung Mietangebot bzw. Neuanmietung</h1>
      <p>Vielen Dank für die Nutzung unseres Online-Services. Folgende Daten haben wir überprüft:</p>
      <table>
        <tbody>
          <tr>
            <td className="font-bold">Anzahl der Personen im Haushalt</td>
            <td className="w-32">{inputData.numberOfPersons}</td>
          </tr>
          <tr>
            <td className="font-bold">Netto- / Kaltmiete</td>
            <td>{inputData.rentPriceCold.toFixed(2)} €</td>
            <td>{rentInLimit ? '✅' : '❌'}</td>
          </tr>
          <tr>
            <td className="font-bold">Betriebskosten</td>
            <td>{inputData.sideCosts.toFixed(2)} €</td>
            <td>{sideCostInLimit ? '✅' : '❌'}</td>
          </tr>
          <tr>
            <td className="font-bold">Ist die Wohnung energetisch saniert?</td>
            <td>{inputData.isEnergeticallyRenovated ? 'Ja' : 'Nein'}</td>
          </tr>
          <tr>
            <td className="font-bold">Ist die Wohnung barrierefrei?</td>
            <td>{inputData.isBarrierFree ? 'Ja' : 'Nein'}</td>
          </tr>
        </tbody>
      </table>
      <section className="text-center">
        <h4>Ergebnis der Prüfung</h4>
        <div>
          Die Bruttokaltmiete ist <span className="font-bold">{(!rentInLimit || !sideCostInLimit) && 'nicht'} angemessen</span>.
        </div>
        <div>{(inputData.rentPriceCold + inputData.sideCosts).toFixed(2)} €</div>
      </section>
      <hr style={{ marginTop: '1rem', marginBottom: '1px' }} />
      <section>
        <h3>Verwendete Gesamtangemessenheitsgrenzen</h3>
        <p>Die Wohnung ist für die angegebene Personenanzahl {(!rentInLimit || !sideCostInLimit) && 'nicht'} angemessen.</p>
        <p>In Dortmund gelten aktuell folgende Obergrenzen für angemessene Kosten der Unterkunft:</p>
        <table>
          <tbody>
            <tr>
              <td className="font-bold">Anzahl der Personen im Haushalt</td>
              <td className="w-32">{inputData.numberOfPersons}</td>
            </tr>
            <tr>
              <td className="font-bold">max. Nettokaltmiete</td>
              <td>{rentLimits.rentPriceCold.toFixed(2)} €</td>
            </tr>
            <tr>
              <td className="font-bold">Betriebskosten*</td>
              <td>{rentLimits.sideCosts.toFixed(2)} €</td>
            </tr>
            <tr>
              <td className="font-bold">max. Bruttokaltmiete</td>
              <td>{(rentLimits.sideCosts + rentLimits.rentPriceCold).toFixed(2)} €</td>
            </tr>
          </tbody>
        </table>
        <p>
          *Betriebskosten sind alle Kosten, die Ihre:m Vermieter:in im Laufe eines Jahres entstehen, um das Haus und das Grundstück, auf dem
          das Haus steht, für Sie als Mieter:in als Wohnraum zur Verfügung stellen zu können. Dazu gehören die kalten Nebenkosten, wie
          beispielsweise Kosten für die Müllabfuhr und die Straßenreinigung, Grundsteuer (oder auch „öffentliche Lasten des Grundstücks“),
          Schornsteinreinigung usw. Für manche Miethäuser fallen weitere Kosten an, wie z. B.: Strom für den Fahrstuhl, Haus- und
          Flurreinigung, Gartenpflege usw.
        </p>
      </section>
      <hr style={{ marginBottom: '1px', marginTop: '1px' }} />
      <section>
        <h3>Hinweis</h3>
        <div>
          <p>
            Es handelt sich um einen Service für Kund:innen des Jobcenter Dortmund bzw. für Personen, die in das Stadtgebiet Dortmund
            umziehen möchten und auf Leistungen nach dem Zweiten Sozialgesetzbuch (SGB II) angewiesen sind. Das Ergebnis dieser Prüfung ist
            nicht verbindlich, stellt keine Genehmigung bzw. Zusage zum Umzug sowie keinen Rechtsanspruch auf etwaige Umzugskosten und/oder
            Mietkautionen dar.
          </p>
          <p>Das Ergebnis dieser Prüfung</p>
          <ul>
            <li>
              ist <span className="font-bold">nicht</span> verbindlich,
            </li>
            <li>
              stellt <span className="font-bold">keine Genehmigung</span> bzw. Zusage zum Umzug sowie
            </li>
            <li>
              <span className="font-bold">keinen Rechtsanspruch</span> auf etwaige Umzugskosten und/oder Mietkautionen dar.
            </li>
          </ul>
        </div>
        <p>
          Für einen rechtsverbindlichen Bescheid reichen Sie bitte das Mietangebot zusammen mit dem Antrag (im Downloadbereich) auf
          Erteilung einer Zusicherung zur Übernahme neuer Kosten der Unterkunft bei Wohnungswechsel beim Jobcenter Dortmund zur Prüfung ein.
        </p>
        <p>
          Dieser Service soll als Anhaltspunkt dienen. Jeder Antrag wird individuell geprüft (Einzelfallprüfung) und kann anders beschieden
          werden.
        </p>
      </section>
    </>
  );
};

export default RentPriceResultStep;
